import { useState, useEffect, useCallback, useRef } from "react";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import { Box } from "@mui/material";
import {
  getMediaHideEdit,
  getMediaFile,
} from "../../../../../services/mediaMgt";
import {
  subOnProcessedMedia,
  type GraphQLObservableSubscription,
} from "../../../../../graphql/api";
import {
  updateMMData,
  createMMData,
  deleteMMData,
} from "../../../../../slices/mediaMgtSlice";
import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import SbuxSubTabs from "../../../../../components/SbuxTabs/SbuxSubTabs";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import SbuxLoader from "../../../../../components/SbuxLoader";
import MediaMgtRow from "./MediaMgtRow";
import MediaMgtDialog from "../../common/MediaMgtDialog";
import { columns, filterColumns } from "../mediaMgt/columns";
import {
  MEDIAMGT_REFRESH_USER_MESSAGE,
  OPERATION,
} from "../../../../../constants/index";
import { logMessage } from "../../../../../utils/amplifyLogger";
import { getInstanceIdSelector } from "../../../../../selectors/userInstanceSelector";
import { getCognitoUserIdSelector } from "../../../../../selectors/authSelector";
import {
  getMediaContentSelector,
  getIsEditOpenSelector,
  getEditDataSelector,
} from "../../../../../selectors/mediaMgtSelector";

interface Language {
  name: string;
  code: string;
  [key: string]: unknown;
}

interface MediaItem {
  languageCode?: string;
  type?: string;
  highLight?: boolean;
  [key: string]: unknown;
}

interface MediaData {
  data?: MediaItem[];
  [key: string]: unknown;
}

interface TabItem {
  name: string;
  value: string;
  [key: string]: unknown;
}

interface Props {
  mediaMgtData: MediaData;
  languages: Language[];
  handleRefrehClick: () => void;
  filteredItem: string[];
  setFilteredItem: (items: string[]) => void;
  mediaMgtReset: boolean;
}

const MediaMgtTabs = ({
  mediaMgtData,
  languages,
  handleRefrehClick,
  filteredItem,
  setFilteredItem,
  mediaMgtReset,
}: Props) => {
  const [languageTabId, setLanguageTabId] = useState("all");
  const [openAddNew, setOpenAddNew] = useState(false);
  const [mediaMgtFilterData, setMediaMgtFilterData] = useState<MediaItem[]>([]);
  const [updatedMediaId, setUpdatedMediaId] = useState<string | null>(null);
  const [openMMUpdateSnackbar, setOpenMMUpdateSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const mediaContent = useAppSelector(getMediaContentSelector);
  const isEditOpen = useAppSelector(getIsEditOpenSelector);
  const editData = useAppSelector(getEditDataSelector);

  const cognitoUserId = useAppSelector(getCognitoUserIdSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);
  const dispatch = useAppDispatch();

  const subs = useRef<GraphQLObservableSubscription[]>([]);
  const subTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMediaSubscription = (data: {
    data?: { onProcessedMedia?: Record<string, unknown> };
  }) => {
    const mediaData = data?.data?.onProcessedMedia;
    if (mediaData && mediaData?.lastUpdatedBy !== cognitoUserId) {
      setUpdatedMediaId((mediaData?.mediaId as string) ?? null);
      const operation = mediaData.__operation;
      switch (operation) {
        case OPERATION.CREATE:
          dispatch(createMMData(mediaData));
          break;
        case OPERATION.UPDATE:
          dispatch(updateMMData(mediaData));
          /* eslint-disable no-case-declarations */
          const mediaFileData = {
            ...mediaData,
            type: "Custom",
            fileName: btoa(
              (mediaData.filePath as string | undefined) ??
                (mediaData.fileName as string | undefined) ??
                "",
            ),
          };
          /* eslint-enable no-case-declarations */
          dispatch(
            getMediaFile(mediaFileData as Parameters<typeof getMediaFile>[0]),
          );
          break;
        case OPERATION.DELETE:
          dispatch(deleteMMData(mediaData));
          break;
      }
      // Only show snackbar if it's not already open
      setOpenMMUpdateSnackbar((prev) => {
        if (!prev) {
          return true;
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    subs.current.forEach((sub) => sub.unsubscribe());
    subs.current = [];

    subTimerRef.current = setTimeout(() => {
      subTimerRef.current = null;
      const onProcessSub = subOnProcessedMedia(instanceId ?? "").subscribe({
        next: handleMediaSubscription,
        error: (err: unknown) =>
          logMessage(
            `Media Management `,
            `Something went wrong in Subscription API => ${err}`,
            `error`,
          ),
      });

      subs.current.push(onProcessSub);
    }, 0);

    return () => {
      if (subTimerRef.current !== null) clearTimeout(subTimerRef.current);
      subs.current.forEach((sub) => sub.unsubscribe());
    };
  }, []);

  useEffect(() => {
    if (mediaMgtData?.data && mediaMgtData.data.length >= 0) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [mediaMgtData]);

  useEffect(() => {
    if (filteredItem && filteredItem.length > 0) {
      const filteredData =
        mediaMgtData.data?.reduce((acc: MediaItem[], cur: MediaItem) => {
          const curValues = Object.values(cur);
          const isExists = curValues.some((f) =>
            filteredItem.includes(f as string),
          );
          if (isExists) {
            acc.push(cur);
          }
          return acc;
        }, []) ?? [];
      setMediaMgtFilterData(filteredData);
    }
    handleSubTabChange(languageTabId);
  }, [mediaMgtData, filteredItem]);

  const handleAddNew = () => {
    setOpenAddNew((prevState) => !prevState);
  };

  const handleSubTabChange = (newValue: string) => {
    setLanguageTabId(newValue);
    let data: MediaItem[] = [];
    if (filteredItem && !filteredItem?.includes("all")) {
      data =
        newValue !== "all"
          ? (mediaMgtData.data ?? []).filter(
              (f: MediaItem) =>
                f.languageCode === newValue &&
                filteredItem?.includes(f.type ?? ""),
            )
          : (mediaMgtData.data ?? []).filter((f: MediaItem) =>
              filteredItem?.includes(f.type ?? ""),
            );
    } else {
      data =
        newValue !== "all"
          ? (mediaMgtData.data ?? []).filter(
              (f: MediaItem) => f.languageCode === newValue,
            )
          : (mediaMgtData.data ?? []);
    }
    setMediaMgtFilterData(data);
  };

  const getLanguageList = (): TabItem[] => {
    return languages && languages.length > 0
      ? languages.reduce((acc: TabItem[], cur: Language) => {
          acc.push({
            name: cur.name,
            value: cur.code,
          });
          return acc;
        }, [])
      : [];
  };
  const closeEdit = () => {
    dispatch(getMediaHideEdit());
  };
  const handleClose = useCallback(() => {
    setOpenMMUpdateSnackbar(false);
    setMediaMgtFilterData((prevData) =>
      prevData.map((item) => ({ ...item, highLight: false })),
    );
  }, []);

  const SubTabsComponent = () => {
    let languageList = getLanguageList();

    if (languageList.length > 0) {
      const defaultLanguage = [
        {
          name: "All",
          value: "all",
        },
      ];
      languageList = [...defaultLanguage, ...languageList];
    }

    const handleSubTabClick = (tab: TabItem) => {
      handleSubTabChange(tab.value);
    };

    return (
      <SbuxSubTabs
        tabList={languageList}
        tabId={languageTabId}
        handleTabClick={handleSubTabClick}
      />
    );
  };

  return (
    <Box sx={FLEX_COL_FILL}>
      {isLoading ? (
        <SbuxLoader />
      ) : (
        <SbuxEnhancedTable
          rows={
            mediaMgtReset && filteredItem.length === 0 ? [] : mediaMgtFilterData
          }
          columns={columns}
          defaultSortingField={"promptName"}
          defaultSortOrder={"asc"}
          RowSource={MediaMgtRow}
          showFooter={true}
          showSearchBar={true}
          isManager={true}
          filterColumns={filterColumns}
          defaultFilterColumns={filteredItem}
          setFilteredItem={setFilteredItem}
          filterTitle={
            <SbuxTranslate>{"Media Management Filter"}</SbuxTranslate>
          }
          showSubTabs
          showAddNewButton
          handleAddNewButtonClick={handleAddNew}
          addNewButtonTitle={<SbuxTranslate>{"Add New"}</SbuxTranslate>}
          SubTabs={SubTabsComponent}
          handleRefreshClick={handleRefrehClick}
          updatedRowId={updatedMediaId}
          isLoading={isLoading}
          footerMarginBottom={"0"}
        />
      )}
      {openAddNew && (
        <MediaMgtDialog
          title={<SbuxTranslate>{`Create a new audio file`}</SbuxTranslate>}
          open={openAddNew}
          toggleDialogOpen={handleAddNew}
          languages={getLanguageList()}
          isAddNew
        />
      )}
      {isEditOpen && (
        <MediaMgtDialog
          title={<SbuxTranslate>{`Edit audio file`}</SbuxTranslate>}
          open={isEditOpen}
          toggleDialogOpen={closeEdit}
          row={editData as Record<string, unknown> | undefined}
          mediaContent={mediaContent ?? undefined}
          languages={getLanguageList()}
          isAddNew={false}
        />
      )}
      {openMMUpdateSnackbar && (
        <SbuxStackSnackbar
          width={380}
          open={openMMUpdateSnackbar}
          message={MEDIAMGT_REFRESH_USER_MESSAGE}
          autoHide={false}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};

export default MediaMgtTabs;
